import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { metasApi } from '../../api/endpoints'
import { LoadingState, ErrorState, EmptyState } from '../../components/ui/EmptyState'
import { GoalStatusBadge, DonoBadge } from '../../components/ui/Badge'
import { Button, IconButton } from '../../components/ui/Button'
import { Card, CardBody, ProgressBar } from '../../components/ui/Card'
import { Input, Select } from '../../components/ui/Input'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { GoalStatus, GOAL_STATUS_LABEL } from '../../lib/enums'
import { formatMoney, formatDate, cn } from '../../lib/utils'
import type { FinancialGoal } from '../../types'

const CHART_TOOLTIP = { background: '#1B2330', border: '1px solid #2D3748', borderRadius: 4, fontSize: 12 }
const AXIS_TICK = { fontSize: 11, fill: '#8B949E' }

const ICONS = ['🎯', '🏠', '🚗', '✈️', '🎓', '💍', '🏖️', '💻', '🚑', '💰']

interface FormState {
  name: string
  icon: string
  targetAmount: string
  currentAmount: string
  targetDate: string
  status: GoalStatus
}

function emptyForm(): FormState {
  return { name: '', icon: '🎯', targetAmount: '', currentAmount: '0', targetDate: '', status: GoalStatus.Active }
}

export function MetasPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<FinancialGoal | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm())
  const [erro, setErro] = useState<string | null>(null)
  const [paraExcluir, setParaExcluir] = useState<FinancialGoal | null>(null)
  const [aporte, setAporte] = useState<{ meta: FinancialGoal; valor: string } | null>(null)
  const [progressoDe, setProgressoDe] = useState<FinancialGoal | null>(null)

  const { data: metas = [], isLoading, isError } = useQuery({ queryKey: ['metas'], queryFn: metasApi.list })

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['metas'] })
    qc.invalidateQueries({ queryKey: ['onboarding'] })
    qc.invalidateQueries({ queryKey: ['dashboard'] })
  }

  const salvar = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        icon: form.icon,
        targetAmount: Number(form.targetAmount),
        currentAmount: Number(form.currentAmount) || 0,
        targetDate: form.targetDate || undefined,
      }
      return editing
        ? metasApi.atualizar(editing.id, { ...payload, status: form.status })
        : metasApi.criar(payload)
    },
    onSuccess: () => {
      invalidate()
      toast.success(editing ? 'Meta atualizada.' : 'Meta criada.')
      setModalOpen(false)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const registrarAporte = useMutation({
    mutationFn: () => metasApi.atualizar(aporte!.meta.id, {
      currentAmount: aporte!.meta.currentAmount + Number(aporte!.valor),
    }),
    onSuccess: () => {
      invalidate()
      toast.success('Valor adicionado à meta.')
      setAporte(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const remover = useMutation({
    mutationFn: (id: string) => metasApi.remover(id),
    onSuccess: () => {
      invalidate()
      toast.success('Meta excluída.')
      setParaExcluir(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function openNew() {
    setEditing(null)
    setForm(emptyForm())
    setErro(null)
    setModalOpen(true)
  }

  function openEdit(m: FinancialGoal) {
    setEditing(m)
    setForm({
      name: m.name,
      icon: m.icon ?? '🎯',
      targetAmount: String(m.targetAmount),
      currentAmount: String(m.currentAmount),
      targetDate: m.targetDate ? m.targetDate.slice(0, 10) : '',
      status: m.status,
    })
    setErro(null)
    setModalOpen(true)
  }

  function submit() {
    if (!form.name.trim() || !form.targetAmount || Number(form.targetAmount) <= 0) {
      setErro('Informe o nome e um valor de meta maior que zero.')
      return
    }
    salvar.mutate()
  }

  return (
    <>
      <PageHeader title="Metas" />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-13 text-muted">{metas.length} meta{metas.length !== 1 ? 's' : ''} cadastrada{metas.length !== 1 ? 's' : ''}</p>
        <Button icon="ri-add-line" onClick={openNew}>Nova meta</Button>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message="Erro ao carregar metas." />
      ) : metas.length === 0 ? (
        <Card><EmptyState icon="ri-focus-3-line" message="Nenhuma meta cadastrada." action={<Button variant="soft-primary" icon="ri-add-line" onClick={openNew}>Nova meta</Button>} /></Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {metas.map((meta) => (
            <Card key={meta.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded bg-primary/10 text-2xl">{meta.icon || '🎯'}</span>
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    <DonoBadge nome={meta.ownerNome} />
                    <GoalStatusBadge status={meta.status} />
                    <IconButton icon="ri-line-chart-line" label={`Ver progresso de ${meta.name}`} onClick={() => setProgressoDe(meta)} />
                    <IconButton icon="ri-pencil-fill" label={`Editar ${meta.name}`} tone="info" onClick={() => openEdit(meta)} />
                    <IconButton icon="ri-delete-bin-5-fill" label={`Excluir ${meta.name}`} tone="danger" onClick={() => setParaExcluir(meta)} />
                  </div>
                </div>
                <h5 className="mt-4 truncate text-15 font-medium text-white">{meta.name}</h5>
                <p className="mt-0.5 flex items-center gap-1 text-13 text-muted">
                  <i className="ri-calendar-event-line" aria-hidden />
                  {meta.targetDate ? `Até ${formatDate(meta.targetDate)}` : 'Sem data alvo'}
                </p>

                <div className="mt-4">
                  <div className="mb-1.5 flex items-center justify-between text-13">
                    <span className="text-muted">Progresso</span>
                    <span className="font-medium text-white">{meta.percentual.toFixed(1)}%</span>
                  </div>
                  <ProgressBar value={meta.percentual} tone={meta.status === GoalStatus.Completed ? 'income' : 'primary'} />
                </div>

                <Button
                  variant="soft-success"
                  size="sm"
                  icon="ri-add-line"
                  className={cn('mt-4 w-full', meta.status !== GoalStatus.Active && 'hidden')}
                  onClick={() => setAporte({ meta, valor: '' })}
                >
                  Adicionar valor
                </Button>
              </CardBody>
              <div className="grid grid-cols-2 border-t border-border/60">
                <div className="border-r border-border/60 px-4 py-3">
                  <p className="text-2xs uppercase text-muted">Atual</p>
                  <p className="text-15 font-semibold text-income">{formatMoney(meta.currentAmount)}</p>
                </div>
                <div className="px-4 py-3 text-right">
                  <p className="text-2xs uppercase text-muted">Meta</p>
                  <p className="text-15 font-semibold text-white">{formatMoney(meta.targetAmount)}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar meta' : 'Nova meta'}
        footer={
          <>
            <Button variant="light" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button icon={editing ? 'ri-save-line' : 'ri-add-line'} onClick={submit} loading={salvar.isPending}>{editing ? 'Salvar' : 'Criar meta'}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <span className="form-label">Ícone</span>
            <div className="flex flex-wrap gap-1.5">
              {ICONS.map((ic) => (
                <button key={ic} type="button" onClick={() => setForm((f) => ({ ...f, icon: ic }))} aria-pressed={form.icon === ic} aria-label={`Ícone ${ic}`}
                  className={cn('flex h-9 w-9 items-center justify-center rounded text-lg transition-colors', form.icon === ic ? 'bg-primary/15 ring-1 ring-primary' : 'bg-surface2 hover:bg-surface3')}>
                  {ic}
                </button>
              ))}
            </div>
          </div>
          <Input label="Nome" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ex: Reserva de emergência" autoFocus />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Valor da meta (R$)" type="number" min={0} step="0.01" value={form.targetAmount} onChange={(e) => setForm((f) => ({ ...f, targetAmount: e.target.value }))} />
            <Input label="Valor já guardado (R$)" type="number" min={0} step="0.01" value={form.currentAmount} onChange={(e) => setForm((f) => ({ ...f, currentAmount: e.target.value }))} />
            <Input label="Data alvo" type="date" value={form.targetDate} onChange={(e) => setForm((f) => ({ ...f, targetDate: e.target.value }))} />
            {editing && (
              <Select label="Situação" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: Number(e.target.value) as GoalStatus }))}>
                {Object.entries(GOAL_STATUS_LABEL).map(([valor, label]) => <option key={valor} value={valor}>{label}</option>)}
              </Select>
            )}
          </div>
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={!!aporte}
        onClose={() => setAporte(null)}
        title={`Adicionar valor · ${aporte?.meta.name ?? ''}`}
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setAporte(null)}>Cancelar</Button>
            <Button icon="ri-add-line" onClick={() => registrarAporte.mutate()} loading={registrarAporte.isPending} disabled={!aporte?.valor}>Adicionar</Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-13 text-muted">
            Guardado hoje: <strong className="text-white">{formatMoney(aporte?.meta.currentAmount ?? 0)}</strong> de {formatMoney(aporte?.meta.targetAmount ?? 0)}
          </p>
          <Input
            label="Quanto adicionar (R$)"
            type="number" min={0} step="0.01"
            value={aporte?.valor ?? ''}
            onChange={(e) => setAporte((a) => a && { ...a, valor: e.target.value })}
            autoFocus
          />
          {aporte && Number(aporte.valor) > 0 && (
            <p className="text-13 text-muted">Novo total: <strong className="text-white">{formatMoney(aporte.meta.currentAmount + Number(aporte.valor))}</strong></p>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!paraExcluir}
        onClose={() => setParaExcluir(null)}
        onConfirm={() => paraExcluir && remover.mutate(paraExcluir.id)}
        loading={remover.isPending}
        message={<>A meta <strong className="text-white">{paraExcluir?.name}</strong> será excluída.</>}
      />

      <ProgressoMetaModal meta={progressoDe} onClose={() => setProgressoDe(null)} />
    </>
  )
}

function ProgressoMetaModal({ meta, onClose }: { meta: FinancialGoal | null; onClose: () => void }) {
  const { data: historico = [], isLoading } = useQuery({
    queryKey: ['meta-historico', meta?.id],
    queryFn: () => metasApi.historico(meta!.id),
    enabled: !!meta,
  })

  const serie = historico.map((h) => ({ label: formatDate(h.data), valor: h.valor }))

  return (
    <Modal open={!!meta} onClose={onClose} title={`Progresso · ${meta?.name ?? ''}`}>
      {isLoading ? <LoadingState /> : serie.length < 2 ? (
        <EmptyState icon="ri-line-chart-line" message="Ainda não há histórico suficiente — volte depois de alguns dias ou aportes." />
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={serie} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
            <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={CHART_TOOLTIP} />
            <Line type="monotone" dataKey="valor" name="Guardado" stroke="#FFB300" strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Modal>
  )
}
